import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import { useAuth } from "../context/AuthContext";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  VideoCameraIcon,
  StarIcon,
  ChevronRightIcon,
  SpeakerWaveIcon,
  CheckIcon,
  HeartIcon,
  SignalIcon,
  CameraIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  UserPlusIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import { HeartIcon as HeartOutlineIcon } from "@heroicons/react/24/outline";
import { Button } from "@radix-ui/themes";
import api from "../services/api";
import { AIActor } from "../types/actor";

// Define CSS for simple animations
const simpleAnimationStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
  
  .animate-pulse {
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes loading-dots {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }
  
  .loading-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin: 0 3px;
    background-color: currentColor;
    animation: loading-dots 1.4s infinite ease-in-out both;
  }
  
  .loading-dot:nth-child(1) {
    animation-delay: 0s;
  }
  
  .loading-dot:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  .loading-dot:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

// Define the possible filter categories
type FilterCategories = {
  gender: string[];
  age: string[];
  ethnicity: string[];
  shot_type: string[];
  niche: string[];
  product_type: string[];
  background: string[];
};

// Define the category map for filter options - map UI labels to database values
const CATEGORY_MAP = {
  gender: {
    "👩 Woman": "Female", // The backend actually uses title case
    "👨 Man": "Male",
  },
  age: {
    "👱 Young Adult": "Young adult",
    "👨‍💼 Early Middle-Aged": "Early middle-aged",
    "👵 Middle-Aged": "Middle-aged",
    "👴 Elderly": "Elderly",
  },
  ethnicity: {
    "👩‍🦰 Caucasian": "Caucasian",
    "👩🏿 Black/African": "African American",
    "👩🏻 East Asian": "East Asian",
    "👩🏽 Hispanic": "Hispanic or Latino",
    "👳 Middle Eastern": "Middle Eastern or North African",
    "👳🏾 South Asian": "South Asian",
    "👩🏼 Southeast Asian": "Southeast Asian",
  },
  shot_type: {
    "🤳 Selfie": "Selfie",
    "👁️ Close-up": "Close Up",
    "👐 Show and Tell": "Show and Tell",
  },
  niche: {
    "💄 Beauty": "Beauty",
    "🧴 Skincare": "Skincare",
    "👕 Fashion": "Fashion",
    "💻 Technology": "Technology",
    "🏋️ Fitness": "Fitness",
    "🥗 Health": "Health & Fitness",
    "🍽️ Food": "Food",
    "🏠 Home": "Household",
    "✈️ Travel": "Travel",
    "💊 Vitamins": "Vitamins & Supplements",
    "🧪 Personal Care": "Personal Care",
    "☕ Beverage": "Beverage",
    "👔 Business": "Business",
    "🔌 Electronics": "Electronics",
    "📱 Consumer Electronics": "Consumer Electronics",
    "🌱 Eco-friendly": "Eco-friendly",
    "🏡 Home Appliances": "Home Appliances",
    "🧬 Health & Personal Care": "Health & Personal Care",
    "🌿 Lifestyle": "Lifestyle",
    "⚽ Sports": "Sports",
    "🧰 General": "General",
  },
  product_type: {
    "🤏 Small Item": "Small",
    "👐 Medium Item": "Medium",
    "📦 Large Item": "Large, Two hands",
    "🙌 Two-handed Item": "Two hands",
    "🪑 Table Display": "Table",
  },
  background: {
    "🏠 Home/Living Room": "Home",
    "🍽️ Kitchen": "Kitchen",
    "🛏️ Bedroom": "Bedroom",
    "🧼 Bathroom": "Bathroom",
    "🏢 Office": "Office",
    "🏬 Store/Mall": "Store",
    "🌳 Outdoors/Park": "Outdoors",
    "🏙️ Urban/City": "Urban",
    "🏝️ Beach": "Beach",
    "🏔️ Nature": "Nature",
    "🏋️ Gym": "Gym",
    "🍽️ Restaurant/Cafe": "Restaurant",
    "🏫 Studio": "Studio",
    "🧘 Wellness/Spa": "Wellness",
  },
};

// Add toggle state for similar bounding box filter - default tolerance value
const SIMILAR_BOX_TOLERANCE = 0.2; // 20% tolerance for considering boxes similar

interface AIActorSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectActors: (actors: AIActor[]) => void;
  currentSelectedActors?: AIActor[];
  itemType: string;
  projectId: string;
  productPrompt?: string;
  initialFilters?: {
    gender: string[];
    ethnicity: string[];
    [key: string]: string[];
  };
  // productImage: string;
}

// Helper function to compare arrays for equality
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Helper function to render filter buttons for a category
const renderFilterButtons = (
  category: keyof typeof CATEGORY_MAP,
  filters: FilterCategories,
  toggleFilter: (category: keyof FilterCategories, value: string) => void,
  isFilterSelected: (category: keyof FilterCategories, value: string) => boolean
) => {
  if (!CATEGORY_MAP[category]) {
    return null; // Return null if category doesn't exist in the map
  }

  return Object.entries(CATEGORY_MAP[category]).map(([label, value]) => {
    // Check if this value is selected
    const selected = isFilterSelected(category, value);
    // console.log(`Filter ${category}:${value} is selected:`, selected);

    return (
      <button
        key={label}
        className={`px-3 py-1 rounded-md border text-sm hover:cursor-pointer transition-colors duration-200 ${
          selected
            ? "bg-blue-500 text-white border-blue-200 dark:border-blue-600"
            : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
        }`}
        onClick={() => toggleFilter(category, value)}
      >
        {label}
      </button>
    );
  });
};

export default function AIActorSelectionDialog({
  isOpen,
  onClose,
  onSelectActors,
  currentSelectedActors = [],
  itemType,
  projectId,
  productPrompt,
  initialFilters,
}: // productImage,
AIActorSelectionDialogProps) {
  // Get user's video credits from auth context
  const { user, isLoading } = useAuth();
  const videosLeft = user?.videos_left ?? 20; // Default to 20 if not available yet

  // State for avatars
  const [actors, setActors] = useState<AIActor[]>([]);
  const [isLoadingActors, setIsLoadingActors] = useState(false);
  const [actorError, setActorError] = useState<string | null>(null);

  // Track product image and properties - directly use productPrompt as initial value if available
  const [productImage, setProductImage] = useState<string | null>(null);

  // Debug output when productPrompt changes
  useEffect(() => {
    console.log("productPrompt changed:", productPrompt);
  }, [productPrompt]);

  // Track product image orientation (landscape = true, portrait = false)
  const [isProductLandscape, setIsProductLandscape] = useState<boolean>(false);

  // Track image orientations (landscape = true, portrait = false)
  const [imageOrientations, setImageOrientations] = useState<
    Record<string, boolean>
  >({});
  // Add similar bounding box filter toggle - on by default
  const [similarBoxFilterEnabled, setSimilarBoxFilterEnabled] =
    useState<boolean>(false);
  // Reference avatar for similar box filtering
  const [referenceActor, setReferenceActor] = useState<AIActor | null>(null);

  // Original image dimensions
  const ORIGINAL_WIDTH = 576;
  const ORIGINAL_HEIGHT = 1024;

  // Add state to track product aspect ratio
  const [productAspectRatio, setProductAspectRatio] = useState<number | null>(
    null
  );

  // Function to calculate percentage-based coordinates
  const calculateBoundingBoxStyle = (actor: AIActor) => {
    // Default values if any coordinate is missing
    const x = actor.x ?? 0;
    const y = actor.y ?? 0;
    const width = actor.width ?? 0;
    const height = actor.height ?? 0;

    // Convert absolute pixel coordinates to percentage of original image dimensions
    const xPercent = (x / ORIGINAL_WIDTH) * 100;
    const yPercent = (y / ORIGINAL_HEIGHT) * 100;
    const widthPercent = (width / ORIGINAL_WIDTH) * 100;
    const heightPercent = (height / ORIGINAL_HEIGHT) * 100;

    return {
      left: `${xPercent}%`,
      top: `${yPercent}%`,
      width: `${widthPercent}%`,
      height: `${heightPercent}%`,
    };
  };

  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12; // Number of actors to display per page
  const observerTarget = useRef<HTMLDivElement>(null);

  // State to track if we're showing only favorites
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<FilterCategories>({
    gender: [],
    age: [],
    ethnicity: [],
    shot_type: [],
    niche: [],
    product_type: [],
    background: [],
  });

  // Apply initial filters when dialog opens and/or initialFilters changes
  useEffect(() => {
    if (isOpen && initialFilters) {
      console.log("Applying initial filters on dialog open:", initialFilters);

      // Log the current filter values from CATEGORY_MAP for debugging
      console.log(
        "Available gender values:",
        Object.values(CATEGORY_MAP.gender)
      );
      console.log(
        "Available ethnicity values:",
        Object.values(CATEGORY_MAP.ethnicity)
      );

      // Force a refresh of the filters by clearing them first, then setting them
      // This ensures the UI will update properly
      setTimeout(() => {
        setFilters((prev) => {
          const newFilters = {
            ...prev,
            gender: initialFilters.gender || [],
            ethnicity: initialFilters.ethnicity || [],
          };
          console.log("Setting filters to:", newFilters);
          return newFilters;
        });
      }, 0);
    }
  }, [isOpen, initialFilters]);

  const [selectedActors, setSelectedActors] = useState<string[]>([]);
  const [hoveredActor, setHoveredActor] = useState<string | null>(null);

  // Add mousePosition state at the top with other states
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Add state for custom background
  const [showCustomBackgroundInput, setShowCustomBackgroundInput] =
    useState(false);
  const [customBackgroundText, setCustomBackgroundText] = useState("");
  const [customBackgrounds, setCustomBackgrounds] = useState<
    Record<string, string>
  >(() => {
    // Load custom backgrounds from localStorage on first render
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("customBackgrounds");
      return saved ? JSON.parse(saved) : {};
    }
    return {};
  });

  // Add ref for custom background input
  const customBackgroundInputRef = useRef<HTMLInputElement>(null);

  // Add the mouse move handler function
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left, // x position within the element
      y: e.clientY - rect.top, // y position within the element
    });
  }, []);

  // State to track which actors have merged images (keeping state management for API functionality)
  const [actorsWithMergedImages, setActorsWithMergedImages] = useState<
    Record<string, boolean>
  >({});

  // Function to periodically assign voices to avatars in the background
  const assignAvatarVoices = useCallback(() => {
    // Use the api service to make the request
    api
      .post("/secret/assign-avatar-voices")
      .then(() =>
        console.log("Avatar voice assignment triggered in background")
      )
      .catch((error) =>
        console.error("Error triggering avatar voice assignment:", error)
      )
      // Make sure to catch the error so it doesn't affect the UI
      .finally(() => {
        // This ensures the promise is properly handled and won't block anything
      });
  }, []);

  // Set up interval to periodically call the voice assignment API
  useEffect(() => {
    if (isOpen) {
      // Call once immediately when the dialog opens
      assignAvatarVoices();

      // Set up recurring calls every 10 seconds
      const intervalId = setInterval(assignAvatarVoices, 10000); // 10000ms = 10 seconds

      // Clean up the interval when the dialog closes
      return () => clearInterval(intervalId);
    }
  }, [isOpen, assignAvatarVoices]);

  // Initialize selected actors only when dialog opens, not when currentSelectedActors changes
  useEffect(() => {
    if (isOpen && currentSelectedActors && currentSelectedActors.length > 0) {
      // Extract IDs from the currentSelectedActors array
      const selectedIds = currentSelectedActors.map((actor) => actor.id);

      // Only set the selected actors when the dialog first opens
      // This prevents re-adding actors that were removed in GenerateVideoPage
      if (selectedActors.length === 0) {
        console.log("Setting initial selected actors:", selectedIds);
        setSelectedActors(selectedIds);
      }
    }
  }, [isOpen]); // Only run when isOpen changes, not when currentSelectedActors changes

  // Toggle filter selection - modified to allow only one selection per category
  const toggleFilter = (category: keyof FilterCategories, value: string) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (updated[category].includes(value)) {
        updated[category] = updated[category].filter((item) => item !== value);
      } else {
        // Replace any existing selection with the new value
        updated[category] = [value];
      }
      return updated;
    });

    // Reset to first page whenever filters change
    setPage(1);
  };

  // Check if a filter is selected
  const isFilterSelected = (
    category: keyof FilterCategories,
    value: string
  ) => {
    const isSelected = filters[category].includes(value);
    // Debug information to help troubleshoot
    if (isSelected) {
      console.log(`Filter category: ${category}, value: ${value} is selected`);
      console.log(`Current filters for ${category}:`, filters[category]);
    }
    return isSelected;
  };

  // Toggle actor selection - update to handle similar box filter
  const toggleActorSelection = (actorId: string) => {
    setSelectedActors((prev) => {
      if (prev.includes(actorId)) {
        // If we're deselecting the reference actor, clear it
        if (referenceActor && referenceActor.id === actorId) {
          setReferenceActor(null);
        }
        return prev.filter((id) => id !== actorId);
      } else {
        // If this is the first selection, set it as the reference actor
        if (prev.length === 0 && similarBoxFilterEnabled) {
          const actor = actors.find((a) => a.id === actorId);
          if (actor) {
            setReferenceActor(actor);
          }
        }
        // Allow multiple selections
        return [...prev, actorId];
      }
    });
  };

  // Toggle favorite status
  const toggleFavorite = (e: React.MouseEvent, actorId: string) => {
    e.stopPropagation(); // Prevent triggering selection
    setActors((prev) =>
      prev.map((actor) =>
        actor.id === actorId
          ? { ...actor, isFavorite: !actor.isFavorite }
          : actor
      )
    );
  };

  // Toggle showing only favorites
  const toggleShowFavorites = () => {
    setShowFavoritesOnly(!showFavoritesOnly);
    // Reset all filters when toggling favorites view
    setFilters({
      gender: [],
      age: [],
      ethnicity: [],
      shot_type: [],
      niche: [],
      product_type: [],
      background: [],
    });
    setSearchQuery("");
    // Reset to first page
    setPage(1);
    setActors([]); // Clear the actors to reset the view
    setHasMore(true); // Reset hasMore flag to trigger a fetch
    // Keep custom backgrounds intact - they're not lost when toggling view
  };

  // Check if user has enough credits for the selected actors
  const notEnoughCredits = selectedActors.length > videosLeft;

  // Disable selection if not enough credits
  const handleActorClick = (actorId: string) => {
    if (selectedActors.includes(actorId)) {
      // Always allow deselecting
      toggleActorSelection(actorId);
    } else if (selectedActors.length < videosLeft) {
      // Only allow selecting if user has enough credits
      toggleActorSelection(actorId);
    }
  };

  // Check if an actor has a merged image
  const checkForMergedImage = async (actorId: string) => {
    if (!projectId) return;

    try {
      const response = await api.get(
        `/api/avatars/merged-image/${actorId}/${projectId}`
      );

      if (response.data && !response.data.not_found) {
        // Actor has a merged image
        setActorsWithMergedImages((prev) => ({
          ...prev,
          [actorId]: true,
        }));
        return true;
      } else {
        // Actor doesn't have a merged image
        setActorsWithMergedImages((prev) => ({
          ...prev,
          [actorId]: false,
        }));
        return false;
      }
    } catch (error) {
      console.error(`Error checking merged image for actor ${actorId}:`, error);
      setActorsWithMergedImages((prev) => ({
        ...prev,
        [actorId]: false,
      }));
      return false;
    }
  };

  // Handle next button click
  const handleNext = () => {
    if (selectedActors.length > 0 && !notEnoughCredits) {
      // First, get actors that were loaded in the current view
      const loadedActorObjects = actors.filter((actor) =>
        selectedActors.includes(actor.id)
      );

      // Get IDs of actors that were loaded
      const loadedActorIds = loadedActorObjects.map((actor) => actor.id);

      // Find IDs of selected actors that are not loaded in the current view
      const unloadedActorIds = selectedActors.filter(
        (id) => !loadedActorIds.includes(id)
      );

      // Initialize the array that will store all selected actors
      let allSelectedActorObjects = [...loadedActorObjects];

      // If there are unloaded actors and we have currentSelectedActors
      if (unloadedActorIds.length > 0 && currentSelectedActors) {
        // Get the previously selected actors that match our unloaded IDs
        const unloadedActorObjects = currentSelectedActors.filter((actor) =>
          unloadedActorIds.includes(actor.id)
        );

        // Add to our result array
        allSelectedActorObjects = [
          ...allSelectedActorObjects,
          ...unloadedActorObjects,
        ];
      }

      // Log for debugging
      console.log("Selected actor IDs:", selectedActors);
      console.log(
        "Loaded actor objects:",
        loadedActorObjects.map((a) => a.id)
      );
      console.log("Unloaded actor IDs:", unloadedActorIds);
      console.log(
        "All selected actor objects:",
        allSelectedActorObjects.map((a) => a.id)
      );

      // Ensure we have the same number of actor objects as selected IDs
      if (allSelectedActorObjects.length !== selectedActors.length) {
        console.warn(
          `Warning: Selected ${selectedActors.length} actors but only found ${allSelectedActorObjects.length} actor objects`
        );
      }

      if (allSelectedActorObjects.length > 0) {
        onSelectActors(allSelectedActorObjects);
        onClose();
      }
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      gender: [],
      age: [],
      ethnicity: [],
      shot_type: [],
      niche: [],
      product_type: [],
      background: [],
    });
    setSearchQuery("");
    setPage(1);
    setActors([]); // Clear actors to force a refresh
    setHasMore(true); // Reset hasMore flag to trigger a fetch
  };

  // Handle custom background submission
  const handleCustomBackgroundSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customBackgroundText.trim()) {
      // Add the custom background to filters and select it
      const customValue = customBackgroundText.trim();
      const displayValue = `🏞️ ${customValue}`;

      // Add to custom backgrounds list
      setCustomBackgrounds((prev) => ({
        ...prev,
        [customValue]: displayValue,
      }));

      // Add to filters
      setFilters((prev) => ({
        ...prev,
        background: [...prev.background, customValue],
      }));

      // Reset the input and hide it
      setCustomBackgroundText("");
      setShowCustomBackgroundInput(false);

      // Reset to first page and trigger a fetch with the new filter
      setPage(1);
      setActors([]); // Clear actors to force a refresh
      setHasMore(true); // Reset hasMore flag to trigger a fetch
    }
  };

  // Focus the input when it becomes visible
  useEffect(() => {
    if (showCustomBackgroundInput && customBackgroundInputRef.current) {
      customBackgroundInputRef.current.focus();
    }
  }, [showCustomBackgroundInput]);

  // Save custom backgrounds to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "customBackgrounds",
        JSON.stringify(customBackgrounds)
      );
    }
  }, [customBackgrounds]);

  // Add a separate loading state for filter changes
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Update fetchAvatars function to handle backend bounding box filtering
  const fetchAvatars = useCallback(
    async (
      loadPage: number,
      append: boolean = false,
      isFilterChange: boolean = false
    ) => {
      // Set loading states based on context
      if (!append) {
        if (isFilterChange) {
          setIsFilterLoading(true);
        } else {
          setIsLoadingActors(true);
        }
      } else {
        setIsFetchingMore(true);
      }

      try {
        // First, get the project info to obtain the product prompt
        let projectProductPrompt = productPrompt;

        if (!projectProductPrompt && projectId) {
          try {
            const projectResponse = await api.get(`/api/projects/${projectId}`);
            if (projectResponse.data && projectResponse.data.product_prompt) {
              projectProductPrompt = projectResponse.data.product_prompt;
            }
          } catch (error) {
            console.error(
              "Failed to fetch project data for product prompt:",
              error
            );
          }
        }

        // Build query parameters for filters
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(loadPage));
        queryParams.append("limit", String(itemsPerPage));

        if (projectProductPrompt) {
          queryParams.append("product_prompt", projectProductPrompt);
        }

        // Only add filters for fields that exist in the Avatar model
        if (filters.gender.length > 0) {
          queryParams.append("gender", filters.gender.join(","));
        }
        if (filters.age.length > 0) {
          queryParams.append("age", filters.age.join(","));
        }
        if (filters.ethnicity.length > 0) {
          queryParams.append("ethnicity", filters.ethnicity.join(","));
        }
        if (filters.shot_type.length > 0) {
          queryParams.append("shot_type", filters.shot_type.join(","));
        }
        if (filters.niche.length > 0) {
          queryParams.append("niche", filters.niche.join(","));
        }
        if (filters.product_type.length > 0) {
          queryParams.append("product_type", filters.product_type.join(","));
        }
        if (filters.background.length > 0) {
          queryParams.append("background", filters.background.join(","));
        }

        // Add search parameter if it exists
        if (searchQuery) {
          queryParams.append("q", searchQuery);
        }

        // Add tag filter for favorites if needed
        if (showFavoritesOnly) {
          queryParams.append("favorites", "true");
        }

        queryParams.append("item_type", itemType);

        // Add similar box filtering parameters if enabled
        if (similarBoxFilterEnabled && itemType === "product") {
          queryParams.append("similar_box_filter", "true");

          // If we have a reference actor, use its bounding box
          if (referenceActor) {
            queryParams.append("reference_x", String(referenceActor.x || 0));
            queryParams.append("reference_y", String(referenceActor.y || 0));
            queryParams.append(
              "reference_width",
              String(referenceActor.width || 0)
            );
            queryParams.append(
              "reference_height",
              String(referenceActor.height || 0)
            );
            queryParams.append("tolerance", String(SIMILAR_BOX_TOLERANCE));
          }
          // If no reference actor but we have a product aspect ratio, send it
          else if (productAspectRatio) {
            queryParams.append(
              "product_aspect_ratio",
              String(productAspectRatio)
            );
          }
          // Fallback: use default aspect ratio based on isProductLandscape
          else if (isProductLandscape !== undefined) {
            const defaultAspectRatio = isProductLandscape ? 1.5 : 0.67;
            queryParams.append(
              "product_aspect_ratio",
              String(defaultAspectRatio)
            );
          }
        }

        const apiUrl = `/api/avatars?${queryParams.toString()}`;

        // Make the API request
        const response = await api.get(apiUrl);

        // Check if there are any avatars in the response
        if (!response.data.avatars || !Array.isArray(response.data.avatars)) {
          console.error("Invalid avatars data:", response.data);
          setHasMore(false);
          if (!append) {
            setActors([]);
          }
          return;
        }

        // Extract pagination info from response
        const totalCount = response.data.total || 0;
        // Determine if we have more items to load
        const receivedCount = response.data.avatars.length;
        const hasMoreItems = receivedCount >= itemsPerPage && receivedCount > 0;

        setTotalCount(totalCount);
        setHasMore(hasMoreItems);

        // Map avatars to AIActor objects using the direct fields from the backend
        const avatars = response.data.avatars.map((avatar: any) => {
          // Store the niche value temporarily
          const nicheValue = avatar.niche || "";

          return {
            id: avatar.id,
            name: avatar.name || "Unnamed Avatar",
            image: avatar.character_image_url,
            gender: avatar.gender || "Male",
            age: avatar.age || "Young Adults",
            ethnicity: avatar.ethnicity || "",
            shot_type: avatar.shot_type || "",
            x: parseFloat(avatar.x) || 0,
            y: parseFloat(avatar.y) || 0,
            width: parseFloat(avatar.width) || 0,
            height: parseFloat(avatar.height) || 0,
            clothing_style: "",
            hand_pose: "",
            background: "",
            niches: [nicheValue], // Add the niche as the first element
            situations: [],
            locations: [],
            isFavorite: false,
            prompt: avatar.prompt || "",
            fullTags: avatar.tags || [],
            preview_image_url: avatar.preview_image_url || "",
            voice_id: avatar.voice_id || "",
          } as AIActor;
        });

        // Update actors state based on whether we're appending or replacing
        if (append) {
          setActors((prev) => {
            // Filter out duplicates (by ID) when appending
            const existingIds = new Set(prev.map((a: AIActor) => a.id));
            const uniqueNewActors = avatars.filter(
              (a: AIActor) => !existingIds.has(a.id)
            );
            return [...prev, ...uniqueNewActors];
          });
        } else {
          setActors(avatars);
        }
      } catch (error) {
        console.error("Error fetching avatars:", error);
        if (!append) {
          setActorError("Failed to load avatars. Please try again.");
          setActors([]);
        }
        setHasMore(false);
      } finally {
        // Reset loading states based on context
        if (!append) {
          if (isFilterChange) {
            setIsFilterLoading(false);
          } else {
            setIsLoadingActors(false);
          }
        } else {
          setIsFetchingMore(false);
        }
      }
    },
    [
      filters,
      searchQuery,
      showFavoritesOnly,
      itemsPerPage,
      similarBoxFilterEnabled,
      referenceActor,
      itemType,
      productAspectRatio,
      isProductLandscape,
    ]
  );

  // Add a ref to track initial loading
  const initialLoadDone = useRef(false);
  // Add state for generating new characters
  const [isGeneratingCharacters, setIsGeneratingCharacters] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  // Track which actors are being generated as new
  const [generatingNewActors, setGeneratingNewActors] =
    useState<boolean>(false);
  // Number of placeholders to show while generating
  const [placeholderCount, setPlaceholderCount] = useState(5);

  // useEffect(() => {
  //   if (
  //     totalCount < 5 &&
  //     !isGeneratingCharacters &&
  //     !generatingNewActors &&
  //     !isFilterLoading &&
  //     isOpen === true
  //   ) {
  //     console.log(
  //       "generating mroe characters",
  //       totalCount,
  //       isGeneratingCharacters,
  //       generatingNewActors,
  //       isFilterLoading
  //     );
  //     generateMoreCharacters();
  //   }
  // }, [totalCount, isGeneratingCharacters, generatingNewActors]);

  // Function to generate new characters using the all-in-one efficient backend route
  const generateMoreCharacters = async () => {
    if (!projectId) {
      setActorError("Project ID is required to generate characters");
      return;
    }

    // Instead of replacing the whole screen, we'll show the loading state
    // alongside existing avatars
    setGeneratingNewActors(true);
    setPlaceholderCount(5); // Default to 5 new avatars
    setGenerationProgress(10);
    setActorError(null);

    console.log("🕒 CHARACTER GENERATION STARTED");

    try {
      // Prepare filter parameters from selected filters
      const filterParams = {};

      // Extract filter values to pass to the API
      Object.entries(filters).forEach(([category, values]) => {
        if (values.length > 0) {
          // Just use the first selected value for each category
          filterParams[category] = values[0];
        }
      });

      // Call the all-in-one generation endpoint
      const generateParams = {
        count: 5,
        product_prompt: productPrompt || "", // Send empty string if undefined
        project_id: projectId,
        item_type: itemType,
        ...filterParams,
      };

      console.log(
        "Calling all-in-one character generation with params:",
        generateParams
      );
      setGenerationProgress(30);

      // Make the API call
      const response = await api.post(
        "/api/characters/generate-all",
        generateParams
      );

      if (!response.data || !response.data.success) {
        throw new Error("Failed to generate characters");
      }

      setGenerationProgress(90);

      // Log the timing information from the backend
      if (response.data.timings) {
        console.log("🕒 CHARACTER GENERATION TIMING SUMMARY (from backend):");
        console.log(
          `📋 Get project info: ${response.data.timings.get_project_info.toFixed(
            2
          )}s (${(
            (response.data.timings.get_project_info /
              response.data.timings.total) *
            100
          ).toFixed(2)}%)`
        );
        console.log(
          `📋 Generate prompts: ${response.data.timings.generate_prompts.toFixed(
            2
          )}s (${(
            (response.data.timings.generate_prompts /
              response.data.timings.total) *
            100
          ).toFixed(2)}%)`
        );
        console.log(
          `📋 Generate images: ${response.data.timings.generate_images.toFixed(
            2
          )}s (${(
            (response.data.timings.generate_images /
              response.data.timings.total) *
            100
          ).toFixed(2)}%)`
        );
        console.log(
          `📋 Object detection: ${response.data.timings.object_detection.toFixed(
            2
          )}s (${(
            (response.data.timings.object_detection /
              response.data.timings.total) *
            100
          ).toFixed(2)}%)`
        );
        console.log(
          `📋 Create avatars: ${response.data.timings.create_avatars.toFixed(
            2
          )}s (${(
            (response.data.timings.create_avatars /
              response.data.timings.total) *
            100
          ).toFixed(2)}%)`
        );
        console.log(`📋 TOTAL: ${response.data.timings.total.toFixed(2)}s`);
      }

      // Log how many avatars were created
      if (response.data.avatars) {
        console.log(
          `Successfully generated ${response.data.avatars.length} characters`
        );
      }

      setGenerationProgress(100);

      // Important change: Instead of refreshing the full list, we'll fetch the new avatars
      // and append them to the existing list
      fetchAvatars(1, true, false);
    } catch (error) {
      console.error("Error generating characters:", error);
      let errorMessage = "Failed to generate characters";

      // Provide more specific error messages based on the error type
      if (error.response && error.response.data && error.response.data.detail) {
        // Use the server-provided error message if available
        errorMessage = `Error: ${error.response.data.detail}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      console.log(`🕒 CHARACTER GENERATION FAILED`);
      setActorError(errorMessage);
    } finally {
      setGeneratingNewActors(false);
      setIsGeneratingCharacters(false);
    }
  };

  // Flag to track if initial auto-generation has been done
  const initialAutoGenerationDone = useRef(false);

  // Effect to load avatars when dialog opens
  useEffect(() => {
    console.log("Dialog open state changed:", {
      isOpen,
      productPrompt,
      initialFilters,
    });

    if (isOpen && !initialLoadDone.current) {
      // Reset state and load avatars
      setPage(1);
      setActors([]);
      setActorError(null);
      setHasMore(true);

      // Reset auto-generation flag when dialog first opens
      initialAutoGenerationDone.current = false;

      // Fetch avatars (not a filter change)
      fetchAvatars(1, false, false);
      initialLoadDone.current = true;

      // Immediately load product image when dialog first opens
      if (projectId) {
        console.log(
          "Dialog first opened with productPrompt, immediately loading product image"
        );
        (async () => {
          try {
            const response = await api.get(`/api/projects/${projectId}`);
            console.log("Project details response:", {
              hasProductImageUrl: !!response.data?.product_image_url,
              productImageUrl: response.data?.product_image_url,
            });

            if (response.data && response.data.product_image_url) {
              console.log(
                "Setting product image URL:",
                response.data.product_image_url
              );
              setProductImage(response.data.product_image_url);

              // Forcing the product image to be preloaded before rendering
              await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                  console.log("Product image loaded successfully:", {
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  });

                  // Check if image is landscape (width > height)
                  const isLandscape = img.naturalWidth > img.naturalHeight;
                  setIsProductLandscape(isLandscape);
                  console.log("Setting isProductLandscape:", isLandscape);

                  // Calculate and store the aspect ratio
                  if (img.naturalHeight > 0) {
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    setProductAspectRatio(aspectRatio);
                    console.log("Setting product aspect ratio:", aspectRatio);
                  }
                  resolve(null);
                };

                img.onerror = (error) => {
                  console.error("Error loading product image:", error);
                  reject(error);
                };

                console.log(
                  "Starting image load for:",
                  response.data.product_image_url
                );
                img.src = response.data.product_image_url;
              });
            } else {
              console.log("No product image URL found in project data");
            }
          } catch (error) {
            console.error("Error directly fetching project details:", error);
          }
        })();
      }
    } else if (!isOpen) {
      // Reset the flag when dialog closes
      initialLoadDone.current = false;
    }

    // Add tracking state to check if we need to auto-generate avatars on initial load
    if (isOpen && !initialAutoGenerationDone.current) {
      // Set a timeout to wait for initial load to complete
      const initialLoadTimeout = setTimeout(() => {
        // Check if we have no actors and no errors after everything has loaded
        if (
          actors.length === 0 &&
          !isLoadingActors &&
          !actorError &&
          !isFilterLoading &&
          (filters.gender.length > 0 || filters.ethnicity.length > 0) // Only generate when filters from product flow are applied
        ) {
          console.log(
            "Initial load: No avatars found with current filters. Would auto-generate 10 avatars here.",
            {
              filters,
              initialFilters,
              actorsLength: actors.length,
            }
          );

          // Mark that we've done the initial auto-generation
          initialAutoGenerationDone.current = true;

          // Code to auto-generate is commented out for debug purposes
          // setPlaceholderCount(10); // Generate 10 actors instead of default 5
          // generateMoreCharacters();
        }
      }, 1500); // Wait a bit to ensure the avatars had time to load

      return () => clearTimeout(initialLoadTimeout);
    }
  }, [isOpen, productPrompt, projectId, fetchAvatars]);

  // Effect to handle filter changes needs to include referenceActor
  useEffect(() => {
    // Skip this effect on initial render or if dialog is closed
    if (!isOpen || !initialLoadDone.current) return;

    // Use a debounce to prevent multiple rapid reloads
    const timeoutId = setTimeout(() => {
      setPage(1);
      fetchAvatars(1, false, true);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    filters,
    searchQuery,
    showFavoritesOnly,
    isOpen,
    fetchAvatars,
    referenceActor,
  ]);

  // We've removed the monitor effect that would generate actors whenever filters change
  // Now we only generate on initial load when there are no actors with the first set of filters

  // Update the intersection observer for infinite scroll to load content earlier
  useEffect(() => {
    if (!isOpen || isFetchingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingActors && !isFetchingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchAvatars(nextPage, true);
        }
      },
      {
        threshold: 0.5, // Increased threshold to trigger earlier
        rootMargin: "300px", // Load content when observer is 300px from viewport
      }
    );

    const currentObserverTarget = observerTarget.current;
    if (currentObserverTarget) {
      observer.observe(currentObserverTarget);
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
    };
  }, [isOpen, isLoadingActors, isFetchingMore, hasMore, page, fetchAvatars]);

  // Handle image load and detect orientation
  const handleImageLoad = (
    actorId: string,
    event: React.SyntheticEvent<HTMLImageElement>
  ) => {
    const img = event.currentTarget;
    // Check if image is landscape (width > height)
    const isLandscape = img.naturalWidth > img.naturalHeight;
    setImageOrientations((prev) => ({
      ...prev,
      [actorId]: isLandscape,
    }));
  };

  // Function to handle product image load and detect orientation
  const handleProductImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement>
  ) => {
    const img = event.currentTarget;
    // Check if image is landscape (width > height)
    const isLandscape = img.naturalWidth > img.naturalHeight;
    setIsProductLandscape(isLandscape);

    // Calculate and store the aspect ratio
    if (img.naturalHeight > 0) {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      setProductAspectRatio(aspectRatio);
      console.log("Product aspect ratio:", aspectRatio);
    }
  };

  // Add loading state to the actors grid
  if (isLoadingActors) {
    return (
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <div
          className="fixed inset-0 bg-black/30 dark:bg-black/50"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-[88rem] h-[94vh] bg-white dark:bg-gray-950 rounded-xl shadow-xl overflow-hidden flex flex-col relative">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">
                  Loading avatars...
                </p>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    );
  }

  // Add error state to the actors grid
  if (actorError) {
    return (
      <Dialog open={isOpen} onClose={onClose} className="relative z-50">
        <div
          className="fixed inset-0 bg-black/30 dark:bg-black/50"
          aria-hidden="true"
        />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-[88rem] h-[94vh] bg-white dark:bg-gray-950 rounded-xl shadow-xl overflow-hidden flex flex-col relative">
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <p className="text-red-500 dark:text-red-400 mb-4">
                  {actorError}
                </p>
                <button
                  onClick={() => {
                    setActorError(null);
                    setIsLoadingActors(true);
                    fetchAvatars(1, false, false);
                  }}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
                >
                  Retry
                </button>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop - onClick added to ensure clicks on the backdrop close the dialog */}
      <div
        className="fixed inset-0 bg-black/80"
        aria-hidden="true"
        onClick={onClose} // Add onClick handler to close dialog when backdrop is clicked
      />

      {/* Full-screen container for centering */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden flex flex-col lg:flex-row relative transition-colors duration-200">
          {/* Close button (always visible in top-right corner) */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 z-10 flex items-center justify-center bg-white/10 dark:bg-black/10 rounded-full p-1 hover:bg-white/20 dark:hover:bg-black/20 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Filter sidebar */}
          <div className="w-full lg:w-72 bg-gray-50 dark:bg-gray-800 p-4 overflow-y-auto border-r border-gray-200 dark:border-gray-700 lg:h-full">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                Choose Avatars
              </Dialog.Title>
            </div>

            {/* Filter buttons for All/Starred
            <div className="mb-4">
              <div className="flex space-x-2">
                <button
                  onClick={() => !showFavoritesOnly && toggleShowFavorites()}
                  className={`px-3 py-1.5 rounded-lg flex items-center justify-center ${
                    !showFavoritesOnly
                      ? "bg-purple-500 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <VideoCameraIcon className="h-4 w-4 mr-1.5" /> All Videos
                </button>
                <button
                  onClick={() => showFavoritesOnly && toggleShowFavorites()}
                  className={`px-3 py-1.5 rounded-lg flex items-center justify-center ${
                    showFavoritesOnly
                      ? "bg-purple-500 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }`}
                >
                  <StarIcon className="h-4 w-4 mr-1.5" /> Starred
                </button>
              </div>
            </div> */}

            {/* Add Similar Bounding Box filter toggle */}
            {itemType === "product" && (
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Similar Bounding Box
                  </span>
                  <button
                    onClick={() => {
                      setSimilarBoxFilterEnabled(!similarBoxFilterEnabled);
                      // Reset page and refresh when toggling filter
                      if (selectedActors.length > 0) {
                        // Force refresh with new filter setting
                        setPage(1);
                        fetchAvatars(1, false, true);
                      }
                    }}
                    className={`px-3 py-1 rounded-md hover:cursor-pointer ${
                      similarBoxFilterEnabled
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {similarBoxFilterEnabled ? "View All" : "View Similar"}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  When on, you will only see avatars that can accurately hold
                  your product.
                </p>
              </div>
            )}

            {/* Search input */}
            <div className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm transition-colors duration-200"
                placeholder="Search avatars..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter sections */}
            <div className="space-y-5">
              {/* Gender filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gender
                </h3>
                <div className="flex flex-wrap gap-2">
                  {renderFilterButtons(
                    "gender",
                    filters,
                    toggleFilter,
                    isFilterSelected
                  )}
                </div>
              </div>

              {/* Age filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Age
                </h3>
                <div className="flex flex-wrap gap-2">
                  {renderFilterButtons(
                    "age",
                    filters,
                    toggleFilter,
                    isFilterSelected
                  )}
                </div>
              </div>

              {/* Ethnicity filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ethnicity
                </h3>
                <div className="flex flex-wrap gap-2">
                  {renderFilterButtons(
                    "ethnicity",
                    filters,
                    toggleFilter,
                    isFilterSelected
                  )}
                </div>
              </div>

              {/* Background filters */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Background Location
                </h3>
                <div className="flex flex-wrap gap-2">
                  {/* Render standard background filters */}
                  {renderFilterButtons(
                    "background",
                    filters,
                    toggleFilter,
                    isFilterSelected
                  )}

                  {/* Render custom background filters */}
                  {Object.entries(customBackgrounds).map(([value, label]) => (
                    <div key={`custom-${value}`} className="relative group">
                      <button
                        className={`px-3 py-1 rounded-md border text-sm hover:cursor-pointer transition-colors duration-200 ${
                          isFilterSelected("background", value)
                            ? "bg-blue-500 text-white border-blue-200 dark:border-blue-600"
                            : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        }`}
                        onClick={() => toggleFilter("background", value)}
                      >
                        {label}
                      </button>
                      {/* Remove button - only shows on hover */}
                      <button
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering toggle

                          // Remove from filters if selected
                          if (isFilterSelected("background", value)) {
                            setFilters((prev) => ({
                              ...prev,
                              background: prev.background.filter(
                                (b) => b !== value
                              ),
                            }));
                          }

                          // Remove from custom backgrounds
                          setCustomBackgrounds((prev) => {
                            const updated = { ...prev };
                            delete updated[value];
                            return updated;
                          });
                        }}
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  {showCustomBackgroundInput ? (
                    <form
                      onSubmit={handleCustomBackgroundSubmit}
                      className="flex items-center mt-2"
                    >
                      <input
                        ref={customBackgroundInputRef}
                        type="text"
                        value={customBackgroundText}
                        onChange={(e) =>
                          setCustomBackgroundText(e.target.value)
                        }
                        onKeyDown={(e) => {
                          // Submit the form on Enter key
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleCustomBackgroundSubmit(e);
                          }
                        }}
                        placeholder="Enter custom background"
                        className="flex-grow py-1 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        type="submit"
                        className="ml-2 p-1 bg-blue-500 text-white rounded hover:bg-blue-600 hover:cursor-pointer"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="ml-1 p-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 hover:cursor-pointer"
                        onClick={() => setShowCustomBackgroundInput(false)}
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </form>
                  ) : (
                    <button
                      className="flex items-center text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:cursor-pointer"
                      onClick={() => setShowCustomBackgroundInput(true)}
                    >
                      <PlusCircleIcon className="h-4 w-4 mr-1" />
                      Add custom background
                    </button>
                  )}
                </div>
              </div>

              {/* Shot type filters */}
              {/* {itemType !== "talking" && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Shot Type
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {renderFilterButtons(
                      "shot_type",
                      filters,
                      toggleFilter,
                      isFilterSelected
                    )}
                  </div>
                </div>
              )} */}

              {/* Niche filters */}
              {/* <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </h3>
                <div className="flex flex-wrap gap-2">
                  {renderFilterButtons(
                    "niche",
                    filters,
                    toggleFilter,
                    isFilterSelected
                  )}
                </div>
              </div> */}

              {/* Product Type filters */}
              {/* {itemType !== "talking" && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Product Size
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {renderFilterButtons(
                      "product_type",
                      filters,
                      toggleFilter,
                      isFilterSelected
                    )}
                  </div>
                </div>
              )} */}

              {/* Clear filters button */}
              {Object.values(filters).some((arr) => arr.length > 0) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center justify-center"
                >
                  <XCircleIcon className="h-4 w-4 mr-1.5" /> Clear all filters
                </button>
              )}
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Avatar grid */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Loading indicator for filter changes - only appears during filter loading */}
              {isFilterLoading && (
                <div className="w-full mb-4">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 dark:bg-blue-400 rounded-full animate-loading-bar"></div>
                  </div>
                  <p className="text-sm text-center text-gray-500 dark:text-gray-400 mt-2">
                    Updating results...
                  </p>
                </div>
              )}

              {isLoadingActors && actors.length === 0 ? (
                <div className="flex justify-center items-center h-full">
                  <div className="flex flex-col items-center">
                    <div className="flex space-x-2">
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                    </div>
                    <p className="mt-6 text-gray-500 dark:text-gray-400">
                      Loading avatars...
                    </p>
                  </div>
                </div>
              ) : isGeneratingCharacters && actors.length === 0 ? (
                <div className="flex flex-col h-full">
                  <div className="mb-6 px-4">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                      <div
                        className="bg-purple-600 h-4 rounded-full transition-all duration-300 ease-in-out"
                        style={{ width: `${generationProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 text-center mt-2">
                      Generating new characters for your product...
                    </p>
                  </div>

                  {/* Avatar placeholders with individual loading spinners */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 px-4">
                    {[...Array(5)].map((_, index) => (
                      <div
                        key={`placeholder-${index}`}
                        className="relative aspect-[9/16] rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800"
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : actorError ? (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <div className="text-red-500 mb-4">
                      <ExclamationCircleIcon className="h-8 w-8 mx-auto" />
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      {actorError}
                    </p>
                    <button
                      className="px-4 py-2 bg-purple-500 text-white rounded-lg"
                      onClick={() => fetchAvatars(1, false, false)}
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {similarBoxFilterEnabled && referenceActor
                      ? `${totalCount} similar avatars available`
                      : `${totalCount > 0 ? totalCount : 0} avatars available`}
                  </h2>

                  {/* Generate more characters button when no avatars are available */}
                  {actors.length === 0 && !isFilterLoading && (
                    <div className="flex flex-col items-center justify-center my-12">
                      <p className="text-gray-600 dark:text-gray-300 mb-4 text-center">
                        Select filters and generate avatars to get started.
                      </p>
                      <button
                        onClick={generateMoreCharacters}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium flex items-center hover:cursor-pointer"
                      >
                        <UserPlusIcon className="h-5 w-5 mr-2" />
                        Generate More Characters
                      </button>
                    </div>
                  )}

                  {/* Create Custom Avatar section */}
                  <div
                    className={`relative ${
                      isFilterLoading
                        ? "opacity-50 transition-opacity duration-200"
                        : ""
                    }`}
                  >
                    <div
                      className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 ${
                        isFilterLoading ? "pointer-events-none" : ""
                      }`}
                    >
                      {/* Show generation progress bar if generating new avatars while displaying existing ones */}
                      {generatingNewActors && (
                        <div className="col-span-full mb-4">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                              style={{ width: `${generationProgress}%` }}
                            ></div>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 text-center mt-2 text-sm">
                            Generating {placeholderCount} new characters...
                            (will take around 20 secs)
                          </p>
                        </div>
                      )}

                      {/* Placeholder loading cards for new characters being generated */}
                      {generatingNewActors &&
                        [...Array(placeholderCount)].map((_, index) => (
                          <div
                            key={`new-placeholder-${index}`}
                            className="relative aspect-[9/16] rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800 animate-pulse"
                          >
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <div className="absolute bottom-2 left-2 right-2 h-6 bg-gray-300 dark:bg-gray-700 rounded"></div>
                          </div>
                        ))}

                      {/* Existing actors */}
                      {actors.map((actor) => (
                        <div
                          key={actor.id}
                          className={`relative aspect-[9/16] rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                            selectedActors.includes(actor.id)
                              ? "ring-4 ring-purple-500 dark:ring-purple-400"
                              : "hover:shadow-md"
                          } ${
                            hoveredActor === actor.id
                              ? "transform scale-[1.02]"
                              : ""
                          }`}
                          onClick={() => handleActorClick(actor.id)}
                          onMouseEnter={() => setHoveredActor(actor.id)}
                          onMouseLeave={() => setHoveredActor(null)}
                        >
                          {/* Actor image with bounding box */}
                          <div className="relative w-full h-full">
                            <img
                              src={actor.image}
                              alt={actor.name}
                              className={`${
                                imageOrientations[actor.id]
                                  ? "h-full w-auto object-cover" // Landscape: fill height and overflow width
                                  : "w-full h-auto object-cover" // Portrait: default behavior
                              }`}
                              loading="lazy"
                              onLoad={(e) => handleImageLoad(actor.id, e)}
                            />

                            {/* Bounding box visualization */}
                            {itemType === "product" && (
                              <div
                                // className="absolute border-2 border-purple-500 pointer-events-none overflow-visible"
                                className="absolute  pointer-events-none overflow-visible"
                                style={{
                                  ...calculateBoundingBoxStyle(actor),
                                  // backgroundColor: "rgba(168, 85, 247, 0.4)",
                                }}
                              >
                                {/* Render product image with debug logging */}
                                {productImage !== null &&
                                productImage !== "" ? (
                                  <>
                                    {console.log(
                                      "Rendering product image in bounding box:",
                                      {
                                        productImage,
                                        productAspectRatio,
                                        isProductLandscape,
                                      }
                                    )}
                                    <img
                                      key={`product-${actor.id}-${Date.now()}`} // Force re-render with unique key
                                      src={productImage}
                                      alt="Product"
                                      className="w-full h-full object-contain"
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                      }}
                                      onLoad={handleProductImageLoad}
                                    />
                                  </>
                                ) : (
                                  console.log(
                                    "No product image to render in bounding box"
                                  )
                                )}
                              </div>
                            )}
                          </div>

                          {/* Selection indicator */}
                          {selectedActors.includes(actor.id) && (
                            <div className="absolute top-2 right-2 bg-purple-500 dark:bg-purple-400 text-white rounded-full h-6 w-6 flex items-center justify-center">
                              <CheckIcon className="h-4 w-4" />
                            </div>
                          )}

                          {/* Favorite button */}
                          <button
                            onClick={(e) => toggleFavorite(e, actor.id)}
                            className="absolute top-2 left-2 text-white"
                          >
                            {actor.isFavorite ? (
                              <HeartIcon className="h-5 w-5 text-yellow-400" />
                            ) : (
                              <HeartOutlineIcon className="h-5 w-5 hover:text-yellow-400" />
                            )}
                          </button>

                          {/* Actor info overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                            <div className="flex flex-wrap gap-1 mt-1">
                              {actor.gender && (
                                <span className="inline-block px-1.5 py-0.5 bg-gray-800/50 text-white text-xs rounded">
                                  {actor.gender}
                                </span>
                              )}
                              {actor.age && (
                                <span className="inline-block px-1.5 py-0.5 bg-gray-800/50 text-white text-xs rounded">
                                  {actor.age}
                                </span>
                              )}
                              {actor.niches && actor.niches.length > 0 && (
                                <span className="inline-block px-1.5 py-0.5 bg-gray-800/50 text-white text-xs rounded">
                                  {actor.niches[0]}
                                </span>
                              )}
                              {actor.background && (
                                <span className="inline-block px-1.5 py-0.5 bg-gray-800/50 text-white text-xs rounded">
                                  {actor.background}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Infinite scroll observer element */}
                      {hasMore && (
                        <div
                          ref={observerTarget}
                          className="col-span-full h-16 flex items-center justify-center"
                        >
                          {isFetchingMore && (
                            <div className="flex space-x-2">
                              <div className="loading-dot"></div>
                              <div className="loading-dot"></div>
                              <div className="loading-dot"></div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Generate more characters button at the end of the list */}
                      {!hasMore && actors.length > 0 && !isFilterLoading && (
                        <div className="col-span-full flex flex-col items-center justify-center py-8">
                          <p className="text-gray-600 dark:text-gray-300 mb-4 text-center">
                            No more avatars available. Generate more?
                          </p>
                          <button
                            onClick={generateMoreCharacters}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium flex items-center hover:cursor-pointer"
                          >
                            <UserPlusIcon className="h-5 w-5 mr-2" />
                            Generate More Characters
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer with selection count and next button */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sticky bottom-0 left-0 right-0 flex justify-between items-center transition-colors duration-200">
              <div>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  {selectedActors.length === 0
                    ? "No avatars selected"
                    : `${selectedActors.length} avatar${
                        selectedActors.length > 1 ? "s" : ""
                      } selected`}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {videosLeft} credits available in your account
                </p>
              </div>
              <button
                onClick={handleNext}
                disabled={
                  selectedActors.length === 0 ||
                  notEnoughCredits ||
                  isLoadingActors
                }
                className={`px-4 py-2 rounded-lg ${
                  selectedActors.length === 0 ||
                  notEnoughCredits ||
                  isLoadingActors
                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-purple-500 hover:bg-purple-600 text-white cursor-pointer"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

// Add the CSS for the loading animation at the end of the file
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.innerHTML = `
    @keyframes pulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }

    .animate-pulse {
      animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes loading-dots {
      0%, 80%, 100% { 
        transform: scale(0);
        opacity: 0; 
      }
      40% { 
        transform: scale(1);
        opacity: 1; 
      }
    }

    .loading-dot {
      width: 10px;
      height: 10px;
      margin: 0 3px;
      border-radius: 50%;
      background-color: #8B5CF6;
      display: inline-block;
      animation: loading-dots 1.4s infinite ease-in-out both;
    }

    .loading-dot:nth-child(1) {
      animation-delay: -0.32s;
    }

    .loading-dot:nth-child(2) {
      animation-delay: -0.16s;
    }
    
    @keyframes shimmer {
      0% {
        transform: translateX(-150%);
      }
      100% {
        transform: translateX(150%);
      }
    }

    @keyframes loading-bar {
      0% {
        width: 0%;
      }
      50% {
        width: 60%;
      }
      100% {
        width: 100%;
      }
    }
    
    .animate-loading-bar {
      animation: loading-bar 2s ease-in-out infinite;
    }
  `;
  document.head.appendChild(styleElement);
}
